import {Spec} from 'immutability-helper';
import React, {UpdateSpec} from 'react';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {makeFormattedTextSearchResultData} from 'notebook/mediocre/formatted-text/FormattedTextSearchResults';
// @ts-expect-error
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {FileAnalyticsParams, getFileAnalyticsMetadata} from 'notebook/util/analytics/FileAnalyticsUtils';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';

import {NoteFillInTextBox} from './constants/FillInTextBoxConstants';
import NoteFillInTextBoxEditable from './FillInTextBoxEditable';

function textBoxSelectionInit(this: NoteEditor, item: NotePart): {selection: BenchlingSelection} {
  const cid = this.props.getCid(item);
  // @ts-expect-error: TODO - getContent doesn't have an obvious typesafe implementation
  const start = this.props.getContent(item).text.length;
  const end = start;
  return {selection: {cid, start, end}};
}

function textBoxSelectionAll(this: NoteEditor, item: NotePart): {selection: BenchlingSelection} {
  const cid = NoteFillInTextBoxEditable.hiddenFocusCid(this.props.getCid(item));
  return {selection: {cid, start: 0, end: 0}};
}

const FILL_IN_TEXT_BOX_PLUGIN_COMPONENT = {
  required: [
    'inputBoxCursorHandler',
    'tabHandler',
    'textSplitHandler',
    'inputBoxDeleteHandler',
    'setSelectionAbove',
    'setSelectionBelow',
    'insertItemHandler',
    'itemUpdateHandler',
    // 'textItemUpdateHandler',
    'moveHandler',
    'copyItem',
    'isLockedWithEditWarn',
  ],

  // When moving into a text box from another element, the entire text box should be selected instead of
  // moving into the text text (this allows the user to treat the text box more like an element that can
  // be moved through than a piece of text). selectionAtStart/End set to all enables this.
  selectionAtStart: textBoxSelectionAll,
  selectionAtEnd: textBoxSelectionInit,
  selectionInit: textBoxSelectionInit,
  selectionAll: textBoxSelectionAll,

  default(): unknown {
    return {text: '', formats: formatters.defaultFormats()};
  },

  make(
    this: NoteEditor,
    item: NotePart & {item: NoteFillInTextBox},
    index: number,
    editorProps: NoteEditorProps,
    itemProps: object,
    searchResultProps: MediocreSearchResultProps
  ): JSX.Element {
    // Note: Do not directly access @props or @state or indirectly through a method call here (D5845)
    const cid = editorProps.getCid(item);
    const fileId = editorProps.fileId;

    return (
      <EditorPropertiesContext.Consumer>
        {({rootFolderId: projectId, templateCollectionId, isConfig}) => {
          const fileAnalyticsParams = {projectId, fileId, templateCollectionId};
          return (
            <NoteFillInTextBoxEditable
              editor={this}
              key={cid}
              cid={cid}
              isConfig={
                isConfig ||
                APIUtil.isEntryTemplate(this.props.fileId) ||
                APIUtil.isSubtemplate(this.props.fileId)
              }
              fileId={fileId}
              textBox={item.item}
              formatHandler={this.formatHandler}
              index={index}
              locked={editorProps.locked}
              isProtected={editorProps.isProtected}
              storeState={editorProps.storeState}
              restoreState={editorProps.restoreState}
              requestSelection={editorProps.requestSelection}
              splitHandler={this.textSplitHandler}
              itemUpdateHandler={this.itemUpdateHandler}
              tabHandler={this.tabHandler}
              moveHandler={this.moveHandler}
              viewer={editorProps.viewer}
              pasteHandler={(index, e) => {
                this.pasteHandler(index, e, fileAnalyticsParams);
              }}
              deleteHandler={this.inputBoxDeleteHandler}
              cursorHandler={this.inputBoxCursorHandler}
              setSelectionAbove={this.setSelectionAbove}
              setSelectionBelow={this.setSelectionBelow}
              insertItemHandler={this.insertItemHandler}
              setClipboard={this.copyItem}
              isLockedWithEditWarn={this.isLockedWithEditWarn}
              {...searchResultProps}
            />
          );
        }}
      </EditorPropertiesContext.Consumer>
    );
  },
};

const NOTE_FILL_IN_TEXT_BOX_PLUGIN: NoteEditorPlugin = {
  type: NoteConstants.NOTE_FILL_IN_TEXT_BOX,
  typeClass: 'is-text-box',
  title: 'Text box',
  icon: 'text',
  component: FILL_IN_TEXT_BOX_PLUGIN_COMPONENT,
  linkable: false,
  canPasteFromOtherTenant: true,
  itemToHTML($el: JQuery, item: NotePart): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    $el.html(formatters.renderHTML(item.text, item.formats, {forceNonEmpty: true}));
    return $el;
  },
  itemFromHTML($el: JQuery, item: NotePart, fileAnalyticsParams: FileAnalyticsParams): NotePart | null {
    const sourceFileId = $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID);
    const destinationFileId = this.props.fileId;

    const isSourceConfigFile =
      APIUtil.apiIdToType(sourceFileId) === ModelTypeConstants.TEMPLATE ||
      APIUtil.apiIdToType(sourceFileId) === ModelTypeConstants.SUBTEMPLATE;

    const isDestinationConfigFile =
      APIUtil.apiIdToType(destinationFileId) === ModelTypeConstants.TEMPLATE ||
      APIUtil.apiIdToType(destinationFileId) === ModelTypeConstants.SUBTEMPLATE;

    if (!(isSourceConfigFile && isDestinationConfigFile)) {
      notifications.warn(`Can only paste text boxes to and from templates or sub-templates.`);
      return null;
    }

    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.INSERT_TEXT_BOX, {
      ...getFileAnalyticsMetadata(fileAnalyticsParams),
    });

    return item;
  },
  itemToText(item: NotePart): string {
    return item.text;
  },
  getContent({item}: NotePart & {item: NoteFillInTextBox}): Pick<
    NoteFillInTextBox,
    'text' | 'formats' | 'provenanceUuid'
  > & {
    blockText: string | null;
  } {
    const {text, formats, provenanceUuid} = item;
    return {text, formats, provenanceUuid, blockText: text};
  },
  setContent({text, formats, name, lastFinalizedValue}: NoteFillInTextBox): Spec<NotePart> {
    return {
      item: {$merge: {text, formats, name, lastFinalizedValue}},
    };
  },
  getText({item: {text, formats}}: NotePart & {item: NoteFillInTextBox}): {text: string; formats: Format} {
    return {text, formats};
  },
  setText({text, formats}: {text: string; formats: Format}): UpdateSpec {
    return {
      item: {
        $merge: {text, formats},
      },
    };
  },
  *getSearchResults(
    item: NotePart & {item: NoteFillInTextBox},
    matcher: TextMatcher
  ): Generator<MediocreSearchResult> {
    for (const match of matcher.findAllMatches(item.item.text)) {
      const [matchedText] = match;
      const start = match.index;
      const end = start + matchedText.length;
      yield {
        index: [item.cid],
        data: makeFormattedTextSearchResultData(start, end),
        selection: {selection: {cid: item.cid, start, end}},
        uuid: uuidv4(),
      };
    }
  },
};

export default NOTE_FILL_IN_TEXT_BOX_PLUGIN;
